/**
 * Boat make/model autocomplete for the boat customization form.
 *
 * Attaches suggestion dropdowns to the [data-boat-field="make"] and
 * [data-boat-field="model"] inputs inside a root element that carries
 * data-autocomplete-src (URL of the {make: [models]} JSON theme asset).
 *
 * Suggestions only — free text always remains valid. This script never
 * touches name/form/required attributes or the submit flow, so the theme's
 * FormData snapshot and inline validation behave exactly as without it.
 * The data asset is fetched once, lazily, on first focus of either field.
 */
(function () {
  'use strict';

  var MAX_ITEMS = 8;

  function initBoatAutocomplete(root) {
    if (!root || root.dataset.boatAutocompleteInit === '1') return;
    var src = root.dataset.autocompleteSrc;
    var makeInput = root.querySelector('input[data-boat-field="make"]');
    var modelInput = root.querySelector('input[data-boat-field="model"]');
    if (!src || (!makeInput && !modelInput)) return; // known-boat products render no visible inputs
    root.dataset.boatAutocompleteInit = '1';

    var dataPromise = null;
    var index = null; // { make: [models] }
    var makeByFolded = null; // lowercased make -> canonical make

    function load() {
      if (!dataPromise) {
        dataPromise = fetch(src)
          .then(function (r) { return r.ok ? r.json() : null; })
          .then(function (json) {
            if (!json) return;
            index = json;
            makeByFolded = {};
            Object.keys(json).forEach(function (make) {
              makeByFolded[make.toLowerCase()] = make;
            });
          })
          .catch(function () { /* no suggestions; form works as before */ });
      }
      return dataPromise;
    }

    function resolveMake() {
      if (!makeByFolded || !makeInput) return null;
      return makeByFolded[makeInput.value.trim().toLowerCase()] || null;
    }

    // ---- suggestion sources ------------------------------------------------

    function rank(candidates, query, cap) {
      var q = query.toLowerCase();
      var starts = [];
      var contains = [];
      for (var i = 0; i < candidates.length; i++) {
        var lower = candidates[i].text.toLowerCase();
        var at = lower.indexOf(q);
        if (at === 0) starts.push(candidates[i]);
        else if (at > 0) contains.push(candidates[i]);
        if (starts.length >= cap) break;
      }
      return starts.concat(contains).slice(0, cap);
    }

    function makeSuggestions(query) {
      return rank(
        Object.keys(index).map(function (m) { return { text: m }; }),
        query,
        MAX_ITEMS
      );
    }

    function modelSuggestions(query) {
      var make = resolveMake();
      if (make) {
        return rank(
          index[make].map(function (m) { return { text: m }; }),
          query,
          MAX_ITEMS
        );
      }
      // Make unknown: search all models, show "Model — Make", fill both on pick.
      var all = [];
      Object.keys(index).forEach(function (mk) {
        index[mk].forEach(function (model) {
          all.push({ text: model, hint: mk });
        });
      });
      return rank(all, query, MAX_ITEMS);
    }

    // ---- dropdown UI -------------------------------------------------------

    var listboxSeq = 0;

    function attach(input, getSuggestions, onPick) {
      if (!input) return;

      var wrap = input.closest('.boat-name-combo') || input.parentElement;
      var list = document.createElement('ul');
      var listId = 'boat-ac-list-' + (++listboxSeq);
      list.id = listId;
      list.className = 'boat-name-suggestions';
      list.setAttribute('role', 'listbox');
      list.hidden = true;
      wrap.appendChild(list);

      input.setAttribute('role', 'combobox');
      input.setAttribute('aria-autocomplete', 'list');
      input.setAttribute('aria-expanded', 'false');
      input.setAttribute('aria-controls', listId);

      var items = [];
      var active = -1;

      function close() {
        list.hidden = true;
        list.innerHTML = '';
        items = [];
        active = -1;
        input.setAttribute('aria-expanded', 'false');
        input.removeAttribute('aria-activedescendant');
      }

      function setActive(i) {
        if (active >= 0 && items[active]) items[active].el.classList.remove('is-active');
        active = i;
        if (active >= 0 && items[active]) {
          items[active].el.classList.add('is-active');
          input.setAttribute('aria-activedescendant', items[active].el.id);
          if (items[active].el.scrollIntoView) {
            items[active].el.scrollIntoView({ block: 'nearest' });
          }
        } else {
          input.removeAttribute('aria-activedescendant');
        }
      }

      function pick(item) {
        input.value = item.text;
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.dispatchEvent(new Event('change', { bubbles: true }));
        if (onPick) onPick(item);
        close();
      }

      function render() {
        var query = input.value.trim();
        if (!index) {
          // Data not loaded yet (e.g. user typed before first focus fired, or
          // focus happened before this script initialized): start the load and
          // re-render once it lands.
          load().then(function () {
            if (index && document.activeElement === input) render();
          });
          return close();
        }
        if (query.length < 2) return close();
        var suggestions = getSuggestions(query);
        // Don't show a single suggestion identical to what's already typed.
        if (
          !suggestions.length ||
          (suggestions.length === 1 &&
            !suggestions[0].hint &&
            suggestions[0].text.toLowerCase() === query.toLowerCase())
        ) {
          return close();
        }
        list.innerHTML = '';
        items = suggestions.map(function (s, i) {
          var li = document.createElement('li');
          li.id = listId + '-item-' + i;
          li.setAttribute('role', 'option');
          li.textContent = s.text;
          if (s.hint) {
            var hint = document.createElement('span');
            hint.className = 'boat-name-suggestion-hint';
            hint.textContent = ' — ' + s.hint;
            li.appendChild(hint);
          }
          // pointerdown fires before the input's blur (and before the parent
          // label refocuses the input), so selection wins over dismissal.
          li.addEventListener('pointerdown', function (event) {
            event.preventDefault();
            pick(s);
          });
          list.appendChild(li);
          return { el: li, data: s };
        });
        list.hidden = false;
        input.setAttribute('aria-expanded', 'true');
        setActive(-1);
      }

      input.addEventListener('focus', function () {
        load().then(function () {
          if (document.activeElement === input) render();
        });
      });
      input.addEventListener('input', render);
      input.addEventListener('blur', function () {
        // Delay so a pointerdown pick on the list lands first.
        setTimeout(close, 100);
      });
      input.addEventListener('keydown', function (event) {
        if (list.hidden) return;
        if (event.key === 'ArrowDown') {
          event.preventDefault();
          setActive((active + 1) % items.length);
        } else if (event.key === 'ArrowUp') {
          event.preventDefault();
          setActive((active - 1 + items.length) % items.length);
        } else if (event.key === 'Enter') {
          if (active >= 0) {
            event.preventDefault();
            pick(items[active].data);
          }
        } else if (event.key === 'Escape') {
          close();
        } else if (event.key === 'Tab') {
          close();
        }
      });
    }

    attach(makeInput, makeSuggestions, null);
    attach(modelInput, modelSuggestions, function (item) {
      if (item.hint && makeInput) {
        makeInput.value = item.hint;
        makeInput.dispatchEvent(new Event('input', { bubbles: true }));
        makeInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }

  function initAll() {
    document.querySelectorAll('[data-autocomplete-src]').forEach(initBoatAutocomplete);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
  // Theme editor re-renders sections; re-scan (init guard prevents doubles).
  document.addEventListener('shopify:section:load', initAll);
})();
